import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HeartPulse,
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Search,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { getApiErrorMessage } from '@/client/utils/errors';
import { useReconciliationQuery } from '@/client/api/notifications-infra/notifications.query';
import { ReconciliationIssue, ReconciliationProblem } from '@/client/types/notifications-infra';
import {
  DELIVERY_BASE,
  DeliveryAccessDenied,
  DeliveryPageShell,
  MESSAGING_TEMPLATES_HREF,
  MESSAGING_TYPES_HREF,
  PROBLEM_COPY,
  formatProviderName,
} from '@/client/components/admin/NotificationsInfra/deliveryUi';

const issueProblems = (issue: ReconciliationIssue): ReconciliationProblem[] => {
  if (issue.problems?.length) return issue.problems;
  if (issue.problem) return [issue.problem];
  return [];
};

const DeliveryReconciliationPage = () => {
  useBreadcrumbs([
    { label: 'Communications', href: `${DELIVERY_BASE}/delivery` },
    { label: 'Notifications' },
    { label: 'Reconciliation' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const reconQ = useReconciliationQuery(canView);
  const [problemFilter, setProblemFilter] = useState<string>('');
  const [q, setQ] = useState('');
  const [showAllTypes, setShowAllTypes] = useState(false);

  const summary = reconQ.data?.summary;
  const issues = reconQ.data?.issues ?? [];
  const unreferenced = reconQ.data?.unreferenced ?? [];
  const types = reconQ.data?.types ?? [];
  const withProblems = summary?.with_problems ?? issues.length;
  const isDefect = withProblems > 0;

  const filteredIssues = useMemo(() => {
    const term = q.trim().toLowerCase();
    return issues.filter((issue) => {
      const problems = issueProblems(issue);
      if (problemFilter && !problems.includes(problemFilter)) return false;
      if (!term) return true;
      const hay = [issue.name_system, issue.name, issue.ntypid, ...problems]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(term);
    });
  }, [issues, problemFilter, q]);

  const visibleTypes = useMemo(() => {
    if (showAllTypes) return types;
    return types.filter((t) => t.deliverable === false || (t.problems?.length ?? 0) > 0);
  }, [types, showAllTypes]);

  if (permLoading) {
    return (
      <DeliveryPageShell loading icon={null} title="Reconciliation" description="">
        {null}
      </DeliveryPageShell>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <DeliveryAccessDenied />
        </div>
      </div>
    );
  }

  return (
    <DeliveryPageShell
      icon={<HeartPulse className="h-4 w-4" />}
      title="Reconciliation"
      description="Can every notification type deliver what its preference toggles promise?"
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => reconQ.refetch()}
          disabled={reconQ.isFetching}
          className="gap-2"
        >
          {reconQ.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      {reconQ.isLoading ? (
        <Skeleton className="h-24 w-full rounded-xl" />
      ) : reconQ.isError ? (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
          {getApiErrorMessage(reconQ.error, 'Failed to load reconciliation.')}
        </div>
      ) : isDefect ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
            <div>
              <p className="text-sm font-semibold text-red-950">
                {withProblems} type{withProblems === 1 ? '' : 's'} cannot deliver what settings promise
              </p>
              <p className="text-xs text-red-900/80">
                This is a real defect, not noise. Fix the mapping or template row, then refresh.
                Types live on{' '}
                <Link to={MESSAGING_TYPES_HREF} className="underline">
                  notification types
                </Link>
                ; provider ids are set on{' '}
                <Link to={MESSAGING_TEMPLATES_HREF} className="underline">
                  local templates
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          <div>
            <p className="text-sm font-semibold text-emerald-900">All checked types are deliverable</p>
            <p className="text-xs text-emerald-800/80">
              Preference toggles match what the provider layer can actually send.
            </p>
          </div>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryCard label="Types checked" value={summary?.types_checked} loading={reconQ.isLoading} />
        <SummaryCard label="Deliverable" value={summary?.deliverable} loading={reconQ.isLoading} tone="ok" />
        <SummaryCard
          label="With problems"
          value={withProblems}
          loading={reconQ.isLoading}
          tone={isDefect ? 'danger' : 'ok'}
        />
        <SummaryCard
          label="Unreferenced maps"
          value={summary?.unreferenced_mapped_templates ?? unreferenced.length}
          loading={reconQ.isLoading}
        />
      </div>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Issues</h2>
        <div className="flex flex-wrap items-center gap-2">
          {['', 'no_template', 'unmapped_email', 'sms_no_text'].map((key) => (
            <button
              key={key || 'all'}
              type="button"
              onClick={() => setProblemFilter(key)}
              className={`rounded-md border px-3 py-1.5 text-sm transition-colors duration-150 ${
                problemFilter === key
                  ? 'border-blue-200 bg-blue-50 text-blue-800'
                  : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {key ? PROBLEM_COPY[key]?.title ?? key : 'All'}
            </button>
          ))}
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter types…"
              className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {reconQ.isLoading ? (
            <div className="space-y-2 p-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : filteredIssues.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">
              {issues.length === 0 ? 'No issues.' : 'No issues match this filter.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Problem</th>
                    <th className="px-4 py-3 font-medium">What breaks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredIssues.map((issue, i) => {
                    const problems = issueProblems(issue);
                    return (
                      <tr key={`${issue.ntypid ?? issue.name_system ?? i}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">
                            {issue.name_system || issue.name || '—'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {issue.ntypid != null ? `#${issue.ntypid}` : ''}
                            {issue.name && issue.name_system ? ` · ${issue.name}` : ''}
                          </p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {problems.map((p) => (
                              <span
                                key={p}
                                className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700"
                              >
                                {PROBLEM_COPY[p]?.title ?? p}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="max-w-md px-4 py-3 text-xs text-gray-600">
                          {issue.detail ||
                            problems.map((p) => PROBLEM_COPY[p]?.description).filter(Boolean).join(' ') ||
                            '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-900">Unreferenced provider templates</h2>
        <p className="text-xs text-gray-500">
          Mapped provider templates that no active notification type consumes.
        </p>
        <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
          {reconQ.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : unreferenced.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">None.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {unreferenced.map((row, i) => {
                const id = row.template_id ?? row.id;
                const provider = row.provider ? String(row.provider) : '';
                return (
                  <li key={`${provider}-${id}-${i}`} className="flex items-center justify-between px-4 py-3 text-sm">
                    <div>
                      <p className="font-medium text-gray-900">
                        {row.name || (id != null ? `Template ${id}` : 'Unnamed')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {provider ? formatProviderName(provider) : 'Provider unknown'}
                        {id != null ? ` · ${id}` : ''}
                      </p>
                    </div>
                    {provider ? (
                      <Link
                        to={`${DELIVERY_BASE}/providers/${encodeURIComponent(provider)}`}
                        className="text-xs font-medium text-blue-700 hover:text-blue-900"
                      >
                        Open provider
                      </Link>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </section>

      {types.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Types</h2>
            <button
              type="button"
              onClick={() => setShowAllTypes((v) => !v)}
              className="text-xs font-medium text-blue-700 hover:text-blue-900"
            >
              {showAllTypes ? 'Show problems only' : `Show all (${types.length})`}
            </button>
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleTypes.map((t, i) => {
                    const ok = t.deliverable !== false && !(t.problems?.length);
                    return (
                      <tr key={`${t.ntypid ?? t.name_system ?? i}`}>
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">
                            {t.name_system || t.name || '—'}
                          </span>
                          {t.ntypid != null ? (
                            <span className="ml-2 text-xs text-gray-400">#{t.ntypid}</span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3">
                          {ok ? (
                            <span className="text-xs font-medium text-emerald-700">Deliverable</span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {(t.problems ?? []).map((p) => (
                                <span
                                  key={p}
                                  className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700"
                                >
                                  {PROBLEM_COPY[p]?.title ?? p}
                                </span>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </DeliveryPageShell>
  );
};

const SummaryCard = ({
  label,
  value,
  loading,
  tone,
}: {
  label: string;
  value?: number;
  loading?: boolean;
  tone?: 'ok' | 'danger';
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <p className="text-xs font-medium text-gray-500">{label}</p>
    {loading ? (
      <Skeleton className="mt-2 h-7 w-16" />
    ) : (
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === 'danger' ? 'text-red-800' : tone === 'ok' ? 'text-emerald-800' : 'text-gray-900'
        }`}
      >
        {value ?? '—'}
      </p>
    )}
  </div>
);

export default DeliveryReconciliationPage;
